/**
 * SearchModal — global command-palette-style search overlay.
 *
 * Opened from the sidebar launcher button or the global Cmd/Ctrl+K shortcut
 * (both go through ``searchStore``). Replaces the old always-visible inline
 * sidebar search with a richer, cross-resource experience:
 *
 *   - Empty query  → "Recent chats" (mirrors the previous sidebar default).
 *   - Typed query  → All / Chats / Files tabs over ``GET /api/search``, with
 *                    the matched term highlighted, message snippets for chat
 *                    hits, file-type icons, and inline thumbnails for images.
 *
 * Results are keyboard-navigable (↑/↓ to move, ↵ to open, Esc to close).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiFetch, getAccessToken } from "@/api/client";
import { openArtifactSmart } from "@/api/files";
import { parseUTC, type Conversation } from "@/api/types";
import { useChatStore } from "@/stores/chatStore";
import { useSearchStore } from "@/stores/searchStore";

/** Conversation hit — a Conversation plus an optional content snippet and,
 *  for message-content matches, the id of the matched message to jump to. */
interface ChatResult extends Conversation {
  snippet?: string;
  match_message_id?: string;
  match_created_at?: string;
}

/** File (artifact) hit. ``file_path`` powers the inline image thumbnail. */
interface FileResult {
  id: string;
  filename: string;
  file_type: string;
  file_path: string;
  conversation_id: string;
  conversation_title: string;
  created_at: string;
  updated_at: string;
}

interface SearchResponse {
  chats: ChatResult[];
  files: FileResult[];
}

type TabKey = "all" | "chats" | "files";

type ResultItem =
  | { kind: "chat"; chat: ChatResult }
  | { kind: "file"; file: FileResult };

/** Extensions we can preview inline via ``/api/files/serve``. */
const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"]);

/** Emoji fallbacks for non-image file types (mirrors ArtifactGallery). */
const FILE_ICONS: Record<string, string> = {
  xlsx: "📊",
  xls: "📊",
  csv: "📄",
  json: "📋",
  pdf: "📕",
  html: "🌐",
  htm: "🌐",
  md: "📝",
  txt: "📄",
  docx: "📘",
  doc: "📘",
  pptx: "📙",
  ppt: "📙",
  zip: "🗜️",
};

/** Relative-ish date label: "Today" / "Yesterday" / "22 Jun". */
function formatDate(iso: string): string {
  const d = parseUTC(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const toDay = (dt: Date) =>
    new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const days = Math.round((toDay(now) - toDay(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

/** Wrap case-insensitive matches of ``query`` in ``text`` with a brand-tinted
 *  bold span so the user sees exactly why a result matched. */
function Highlight({ text, query }: { text: string; query: string }): ReactNode {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  let idx = lower.indexOf(ql, cursor);
  while (idx !== -1) {
    if (idx > cursor) parts.push(text.slice(cursor, idx));
    parts.push(
      <mark key={key++} className="bg-transparent text-jove font-semibold">
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    cursor = idx + q.length;
    idx = lower.indexOf(ql, cursor);
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

/** Open an artifact: native OS app on the desktop, an authed download-blob in a
 *  browser (via the shared helper) — never the unauthenticated /serve. */
async function openArtifact(id: string): Promise<void> {
  await openArtifactSmart(id);
}

/** Speech-bubble glyph for chat results. */
function ChatGlyph() {
  return (
    <svg className="h-4 w-4 text-space-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
  );
}

/** The leading icon for a file row — image thumbnail or emoji tile.
 *
 * The thumbnail loads via the **authed, tenant-scoped** ``serve-conv`` route
 * (bearer fetch → blob), NOT the unauthenticated ``/api/files/serve?path`` —
 * which streams any tenant's exports to anyone (the residual we're closing).
 * Only files under this conversation's exports dir get a thumbnail; anything
 * else, or a failed/forbidden fetch, falls back to the emoji tile. */
function FileIcon({ file }: { file: FileResult }) {
  const ext = file.file_type?.toLowerCase() ?? "";
  const canThumb = IMAGE_EXTS.has(ext) && !!file.file_path && !!file.conversation_id;
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!canThumb) return;
    // Derive the conversation-relative path; only exports-scoped files resolve.
    const marker = `/exports/${file.conversation_id}/`;
    const idx = file.file_path.indexOf(marker);
    if (idx < 0) return;
    const rel = file.file_path.slice(idx + marker.length);
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await fetch(
          `/api/files/conv/${encodeURIComponent(file.conversation_id)}/serve?rel=${encodeURIComponent(rel)}`,
          { headers: { Authorization: `Bearer ${getAccessToken()}` } },
        );
        if (!res.ok || cancelled) return;
        objectUrl = URL.createObjectURL(await res.blob());
        if (cancelled) { URL.revokeObjectURL(objectUrl); return; }
        setSrc(objectUrl);
      } catch { /* fall back to the emoji tile */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [canThumb, file.file_path, file.conversation_id]);

  if (canThumb && src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-9 w-9 rounded-lg object-cover border border-space-30 bg-space-20 shrink-0"
      />
    );
  }
  return (
    <div className="h-9 w-9 rounded-lg bg-jove-30 flex items-center justify-center shrink-0">
      <span className="text-base">{FILE_ICONS[ext] ?? "📎"}</span>
    </div>
  );
}

export function SearchModal() {
  const open = useSearchStore((s) => s.open);
  const closeSearch = useSearchStore((s) => s.closeSearch);
  const openConversation = useChatStore((s) => s.openConversation);
  const conversations = useChatStore((s) => s.conversations);

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<TabKey>("all");
  const [chats, setChats] = useState<ChatResult[]>([]);
  const [files, setFiles] = useState<FileResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const hasQuery = query.trim().length > 0;

  // Most-recent conversations for the empty-query default view (pure recency,
  // independent of the sidebar's pinned-first ordering).
  const recentChats = useMemo(
    () =>
      [...conversations]
        .sort((a, b) => parseUTC(b.updated_at).getTime() - parseUTC(a.updated_at).getTime())
        .slice(0, 7),
    [conversations],
  );

  // The flat list currently rendered — drives keyboard nav + Enter activation.
  const items: ResultItem[] = useMemo(() => {
    if (!hasQuery) return recentChats.map((c) => ({ kind: "chat" as const, chat: c }));
    if (tab === "chats") return chats.map((c) => ({ kind: "chat" as const, chat: c }));
    if (tab === "files") return files.map((f) => ({ kind: "file" as const, file: f }));
    // "All" — interleave both kinds by recency so the freshest hit leads.
    const merged: Array<{ item: ResultItem; ts: string }> = [
      ...chats.map((c) => ({ item: { kind: "chat" as const, chat: c }, ts: c.updated_at })),
      ...files.map((f) => ({ item: { kind: "file" as const, file: f }, ts: f.updated_at })),
    ];
    merged.sort((a, b) => parseUTC(b.ts).getTime() - parseUTC(a.ts).getTime());
    return merged.map((m) => m.item);
  }, [hasQuery, recentChats, tab, chats, files]);

  // Reset to a clean palette each time it opens, and focus the input.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setTab("all");
    setChats([]);
    setFiles([]);
    setSelected(0);
    // Defer focus until after the element is painted.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced fetch. ``reqIdRef`` guards against out-of-order responses so a
  // slow earlier query can't overwrite a faster later one.
  const runSearch = useCallback(async (q: string) => {
    const reqId = ++reqIdRef.current;
    setSearching(true);
    try {
      const res = await apiFetch<SearchResponse>(`/search?q=${encodeURIComponent(q)}`);
      if (reqId !== reqIdRef.current) return;
      setChats(res.chats ?? []);
      setFiles(res.files ?? []);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setChats([]);
      setFiles([]);
    } finally {
      if (reqId === reqIdRef.current) setSearching(false);
    }
  }, []);

  const handleChange = (value: string) => {
    setQuery(value);
    setSelected(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      reqIdRef.current++; // cancel any in-flight response
      setChats([]);
      setFiles([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => runSearch(value.trim()), 250);
  };

  // Clamp selection when the list shrinks, and keep the active row in view.
  useEffect(() => {
    if (selected >= items.length) setSelected(Math.max(0, items.length - 1));
  }, [items.length, selected]);

  useEffect(() => {
    rowRefs.current[selected]?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const activate = useCallback(
    (item: ResultItem) => {
      if (item.kind === "chat") {
        // If the hit came from a message body, jump to that message
        // (WhatsApp-style); a title-only hit just opens at the bottom.
        const anchorMessageId = item.chat.match_message_id;
        openConversation(
          item.chat.id,
          anchorMessageId ? { anchorMessageId } : undefined,
        );
        closeSearch();
      } else {
        void openArtifact(item.file.id);
        closeSearch();
      }
    },
    [openConversation, closeSearch],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeSearch();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(items.length - 1, s + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(0, s - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[selected];
      if (item) activate(item);
    }
  };

  if (!open) return null;

  const tabs: Array<{ key: TabKey; label: string; count: number }> = [
    { key: "all", label: "All", count: chats.length + files.length },
    { key: "chats", label: "Chats", count: chats.length },
    { key: "files", label: "Files", count: files.length },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4">
      {/* Backdrop — fades in alongside the card (rather than popping a frame
          early) so the entrance reads as one cohesive motion. */}
      <div
        className="absolute inset-0 bg-black/40 animate-[fadeIn_150ms_ease-out] [animation-fill-mode:both] motion-reduce:animate-none"
        onClick={closeSearch}
      />

      {/* Palette. ``animation-fill-mode:both`` holds the scaled-down/transparent
          start state through any pre-animation frame, which kills the
          occasional entrance flicker; ``will-change`` promotes it to its own
          layer so the scale doesn't repaint the dimmed backdrop. */}
      <div
        className="relative w-[640px] max-w-full bg-surface rounded-2xl shadow-modal border border-space-30
                   overflow-hidden flex flex-col max-h-[76vh]
                   animate-[scaleIn_150ms_ease-out] [animation-fill-mode:both] [will-change:transform,opacity]
                   motion-reduce:animate-none"
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        {/* Search input row */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-space-30 shrink-0">
          <svg
            className="h-4 w-4 text-space-40 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search chats and files..."
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-sm text-space placeholder-space-40
                       focus:outline-none"
          />
          {hasQuery && (
            <button
              onClick={() => handleChange("")}
              className="text-xs font-medium text-space-40 hover:text-space transition-colors"
            >
              Clear
            </button>
          )}
          <div className="w-px h-4 bg-space-30" />
          <button
            onClick={closeSearch}
            className="p-0.5 text-space-40 hover:text-space rounded-md transition-colors"
            aria-label="Close search"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs — only meaningful once a query is typed (matches the empty
            "Recent chats" state, which has no tabs). */}
        {hasQuery && (
          <div className="flex items-center gap-1 px-3 py-2 border-b border-space-30 shrink-0">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => {
                  setTab(t.key);
                  setSelected(0);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
                  ${tab === t.key ? "bg-jove-30 text-jove" : "text-space-40 hover:text-space hover:bg-space-20"}`}
              >
                {t.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                    ${tab === t.key ? "bg-primary text-on-primary" : "bg-space-20 text-space-40"}`}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto scrollbar-on-hover py-2 min-h-[120px]">
          {!hasQuery && (
            <p className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-wider text-space-40 font-medium">
              Recent chats
            </p>
          )}

          {searching && items.length === 0 ? (
            <div className="space-y-1 px-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="px-2 py-2.5">
                  <div className="h-4 shimmer rounded w-3/4 mb-1.5" />
                  <div className="h-3 shimmer rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 px-4">
              <div className="w-10 h-10 rounded-xl bg-space-20 flex items-center justify-center mx-auto mb-2.5">
                <svg className="w-5 h-5 text-space-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-xs text-space-40">
                {hasQuery ? <>No results for &ldquo;{query.trim()}&rdquo;</> : "No conversations yet."}
              </p>
            </div>
          ) : (
            <div className="px-2 space-y-0.5">
              {items.map((item, idx) => {
                const isSel = idx === selected;
                const rowClass = `w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg transition-colors
                  ${isSel ? "bg-jove-10" : "hover:bg-space-20"}`;
                if (item.kind === "chat") {
                  const c = item.chat;
                  return (
                    <button
                      key={`c-${c.id}`}
                      ref={(el) => (rowRefs.current[idx] = el)}
                      onClick={() => activate(item)}
                      onMouseMove={() => setSelected(idx)}
                      className={rowClass}
                    >
                      <div className="h-9 w-9 rounded-lg bg-space-20 flex items-center justify-center shrink-0">
                        <ChatGlyph />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-space truncate">
                          <Highlight text={c.title} query={query} />
                        </p>
                        {c.snippet ? (
                          <p className="text-[11px] text-space-40 truncate mt-0.5">
                            <Highlight text={c.snippet} query={query} />
                          </p>
                        ) : (
                          <p className="text-[11px] text-space-40 mt-0.5">Chat</p>
                        )}
                      </div>
                      {/* ADR-0009: admin-wide search — label hits from other
                          users' chats with their owner. Absent on own chats. */}
                      {c.owner_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-jove-10 text-jove shrink-0 max-w-[90px] truncate">
                          {c.owner_name}
                        </span>
                      )}
                      <span className="text-[11px] text-space-40 shrink-0">{formatDate(c.updated_at)}</span>
                    </button>
                  );
                }
                const f = item.file;
                return (
                  <button
                    key={`f-${f.id}`}
                    ref={(el) => (rowRefs.current[idx] = el)}
                    onClick={() => activate(item)}
                    onMouseMove={() => setSelected(idx)}
                    className={rowClass}
                  >
                    <FileIcon file={f} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-space truncate">
                        <Highlight text={f.filename} query={query} />
                      </p>
                      <p className="text-[11px] text-space-40 truncate mt-0.5">
                        {f.file_type?.toUpperCase()}
                        {f.conversation_title ? ` · ${f.conversation_title}` : ""}
                      </p>
                    </div>
                    <span className="text-[11px] text-space-40 shrink-0">{formatDate(f.updated_at)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
